import React, { useMemo, useState } from "react";
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from "@mui/material";
import TimesheetEditor from "../../components/timesheets/TimesheetEditor";
import { Timesheet } from "../../pages/types/timesheet";
import { createBlankTimesheet, deleteTimesheet, getWeekStart, listTimesheetsByEmployee, upsertTimesheet, calcTotalHours } from "../../pages/services/timesheetsApi";
// ✅ Plug into YOUR auth context (change these 2 lines only)
import { useAuthState } from "../../context/auth-context";
// Expecting auth-context exports something like: { user: { id, name, role } }

export default function MyTimesheets() {
    const auth = useAuthState() as any;

    const user = auth.userDetails || auth.user || auth.details || null;

    const userId =
        user?.id || user?.userId || user?.employeeId || "";

    const userName =
        user?.name || user?.fullName || user?.username || "";
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState<Timesheet | null>(null);
    const [weekDate, setWeekDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [, force] = useState(0);

    const mine: Timesheet[] = useMemo(
        () => listTimesheetsByEmployee(user?.id || ""),
        [user?.id, force]
    );

    const openNew = () => {
        if (!userId) {
            alert("User info not loaded. Please login again.");
            return;
        }

        const weekStart = getWeekStart(weekDate);

        const ts = createBlankTimesheet({
            employeeId: userId,
            employeeName: userName || "Employee",
            weekStart,
        });

        setActive(ts);
        setOpen(true);
    };

    const statusColor = (s: string) => {
        if (s === "SUBMITTED") return "info";
        if (s === "APPROVED") return "success";
        if (s === "REJECTED") return "error";
        return "default";
    };

    return (
        <Stack spacing={2}>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>My Timesheets</Typography>

            <Card>
                <CardContent>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                        <TextField
                            label="Pick any date in week"
                            type="date"
                            value={weekDate}
                            onChange={(e) => setWeekDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ maxWidth: 260 }}
                        />
                        <Button variant="contained" onClick={openNew}>Create Timesheet (Week)</Button>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Saved locally (browser) • wire to backend later
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>History</Typography>
                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1.25}>
                        {mine.length === 0 && <Typography sx={{ opacity: 0.75 }}>No timesheets yet.</Typography>}

                        {mine.map((t) => (
                            <Card key={t.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                                        <Typography sx={{ fontWeight: 800 }}>Week Start: {t.weekStart}</Typography>
                                        <Chip size="small" label={t.status} color={statusColor(t.status) as any} />
                                        <Typography sx={{ opacity: 0.75 }}>Total hours: {calcTotalHours(t)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Stack direction="row" spacing={1}>
                                            <Button variant="outlined" onClick={() => { setActive(t); setOpen(true); }}>Open</Button>
                                            <Button
                                                color="error"
                                                variant="text"
                                                onClick={() => {
                                                    if (window.confirm("Delete this timesheet?")) {
                                                        deleteTimesheet(t.id);
                                                        force((x) => x + 1);
                                                    }
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontWeight: 900 }}>
                    Timesheet • {active?.weekStart} • {user?.name}
                </DialogTitle>
                <DialogContent dividers>
                    {active && <TimesheetEditor value={active} onChange={setActive} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>

                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (!active) return;
                            const saved = upsertTimesheet(active);
                            setActive(saved);
                            alert("Saved (DRAFT)");
                            force((x) => x + 1);
                        }}
                    >
                        Save Draft
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => {
                            if (!active) return;
                            const saved = upsertTimesheet({ ...active, status: "SUBMITTED" });
                            setActive(saved);
                            alert("Submitted!");
                            force((x) => x + 1);
                        }}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}